import { useJsApiLoader, GoogleMap, Marker, OverlayView, MarkerClusterer } from '@react-google-maps/api';
export * from '@react-google-maps/api';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Notice, ActivityIndicator, formatUSCurrencyNum, Text } from '@amtrav/view-components';
import styled, { createGlobalStyle, keyframes, css } from 'styled-components';
import { getThemeProp, MatchMedia } from '@amtrav/themes';
import { Box, Flex } from '@rebass/grid';
import { IconWarningTriangle } from '@amtrav/icons';
import { getTokenValue, THEME_TOKENS, SPACE } from '@amtrav/theme-system';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

const Container = styled.div`
  display: flex;
  padding: 4px 12px;
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
  position: absolute;
  width: 200px;
  bottom: 64px;
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 10px;
  justify-content: space-between;
`;
const InfoContainer = styled(Box)`
  align-items: center;
  display: flex;

  & p {
    font-size: 12px;
    margin: 0px;
    font-weight: bold;
  }
`;
const ColorCircle = styled.div`
  height: 11px;
  width: 11px;
  border-radius: 50%;
  background-color: ${_ref => {
  let {
    color
  } = _ref;
  return color;
}};
  margin-right: 7px;
  margin-left: 1px;
`;
const PolicyLegend = () => {
  return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(InfoContainer, null, /*#__PURE__*/React.createElement(ColorCircle, {
    color: "#5FA444"
  }), /*#__PURE__*/React.createElement("p", null, "In Policy")), /*#__PURE__*/React.createElement(InfoContainer, null, /*#__PURE__*/React.createElement(Flex, {
    mr: "5px"
  }, /*#__PURE__*/React.createElement(IconWarningTriangle, {
    color: "#e68e16"
  })), /*#__PURE__*/React.createElement("p", null, "Out of Policy")));
};

const StyledCheckboxContainer = styled.div`
  bottom: 24px;
  text-align: center;
  position: absolute;
  right: 0;
  left: 0;
  width: 328px;
  margin: 0 auto;
`;
const FilterCheckbox = styled(Checkbox)`
  padding: 10px 32px;
  display: flex;
  align-items: center;
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
  font-weight: 600;
  font-size: 16px;
  color: ${getThemeProp('global.link--color')};
  border-radius: 2px;
  width: fit-content;
  margin-right: auto;
  margin-left: auto;
  transition: background 0.3s;

  &:hover {
    background-color: ${getThemeProp('global.light--color')};
    cursor: pointer;
  }
`;
const FilterByMapCheckbox = _ref => {
  let {
    filterByMapOnChange,
    filterByMapCheckboxValue
  } = _ref;
  return /*#__PURE__*/React.createElement(StyledCheckboxContainer, null, /*#__PURE__*/React.createElement(FilterCheckbox, {
    label: "Filter list when map moves",
    checked: filterByMapCheckboxValue,
    onChange: () => {
      filterByMapOnChange(!filterByMapCheckboxValue);
    }
  }));
};
FilterByMapCheckbox.propTypes = {
  filterByMapOnChange: PropTypes.func.isRequired,
  filterByMapCheckboxValue: PropTypes.bool.isRequired
};

const mapContainerStyle = {
  height: '100%',
  width: '100%'
};
const GlobalMapStyles = createGlobalStyle`
  iframe + div { border:none !important; }
`;
const FullScreenButton = styled.button`
  background: none rgb(255, 255, 255);
  border: 0px;
  margin: 10px;
  padding: 0px;
  text-transform: none;
  appearance: none;
  position: absolute;
  cursor: pointer;
  user-select: none;
  border-radius: 2px;
  height: 36px;
  width: 36px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
  overflow: hidden;
  bottom: 28px;
  left: 0px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 18px;
    width: 18px;
  }
`;
const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;
const GoogleMaps = _ref => {
  var _process$env$GOOGLE_M, _process;
  let {
    children,
    loaderProps,
    zoom = 3,
    center = {
      lat: 37.0902,
      lng: -95.7129
    },
    policyLegend = false,
    filterByMapCheckboxValue = false,
    showFilterByMapCheckbox = false,
    showFullScreenButton = false,
    onFullScreenButtonTap = () => {},
    filterByMapOnChange,
    ...props
  } = _ref;
  const {
    isLoaded,
    loadError
  } = useJsApiLoader({
    googleMapsApiKey: (_process$env$GOOGLE_M = (_process = process) === null || _process === void 0 || (_process = _process.env) === null || _process === void 0 ? void 0 : _process.GOOGLE_MAPS_API_KEY) !== null && _process$env$GOOGLE_M !== void 0 ? _process$env$GOOGLE_M : window.GOOGLE_MAPS_PUBLIC_API_KEY,
    ...loaderProps
  });
  const renderMap = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GlobalMapStyles, null), /*#__PURE__*/React.createElement(MapContainer, {
      id: "map-container"
    }, /*#__PURE__*/React.createElement(GoogleMap, _extends({
      mapContainerStyle: mapContainerStyle,
      zoom: zoom,
      center: center
    }, props), children), policyLegend && /*#__PURE__*/React.createElement(PolicyLegend, null), showFilterByMapCheckbox && /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(FilterByMapCheckbox, {
      filterByMapCheckboxValue: filterByMapCheckboxValue,
      filterByMapOnChange: filterByMapOnChange
    })), showFullScreenButton && /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(FullScreenButton, {
      onClick: onFullScreenButtonTap,
      draggable: "false",
      "aria-label": "Toggle fullscreen view",
      title: "Toggle fullscreen view",
      type: "button"
    }, /*#__PURE__*/React.createElement("img", {
      // eslint-disable-next-line max-len
      src: "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2018%2018%22%3E%3Cpath%20fill%3D%22%23666%22%20d%3D%22M0%200v6h2V2h4V0H0zm16%200h-4v2h4v4h2V0h-2zm0%2016h-4v2h6v-6h-2v4zM2%2012H0v6h6v-2H2v-4z%22/%3E%3C/svg%3E",
      alt: ""
    })))));
  };
  if (loadError) {
    return /*#__PURE__*/React.createElement(Notice, null, "Something is wrong with map");
  }
  return isLoaded ? renderMap() : /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "center",
    alignItems: "center",
    height: "100%"
  }, /*#__PURE__*/React.createElement(ActivityIndicator, null));
};
GoogleMaps.propTypes = {
  /**
   * react-google-maps/api components ONLY
   */
  children: PropTypes.node,
  /**
   * Props for useJsApiLoader
   */
  loaderProps: PropTypes.shape({
    channel: PropTypes.string,
    googleMapsClientId: PropTypes.string,
    id: PropTypes.string,
    language: PropTypes.string,
    libraries: PropTypes.string,
    mapIds: PropTypes.string,
    nonce: PropTypes.string,
    preventGoogleFontsLoading: PropTypes.bool,
    region: PropTypes.string,
    version: PropTypes.string
  }),
  /**
   * Map zoom, this is required but we provided a default so you can render without issues.
   *
   * 0 = Max Zoom out
   *
   * 22 =  Max zoom in
   *
   * For formula to calculate zoom if needed.
   * https://gis.stackexchange.com/questions/7430/what-ratio-scales-do-google-maps-zoom-levels-correspond-to
   */
  zoom: PropTypes.number,
  /**
   * Center map, this is required but we provided a default so you can render without issues.
   * https://stackoverflow.com/questions/10634199/find-center-of-multiple-locations-in-google-maps
   */
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  /**
   * Currently not being used and may be deprecated.
   */
  policyLegend: PropTypes.bool,
  /**
   * Checkbox with label "Filter list when map moves" will appear on bottom of map
   */
  showFilterByMapCheckbox: PropTypes.bool,
  /**
   * You should manage your own state and pass to checkbox value prop
   */
  filterByMapCheckboxValue: PropTypes.bool,
  /**
   * CB function on checkbox chnage
   */
  filterByMapOnChange: PropTypes.func,
  showFullScreenButton: PropTypes.bool,
  onFullScreenButtonTap: PropTypes.func
};

const DotsContainer = styled.div`
  display: flex;
  padding: 4px;
  width: 100%;
`;
const Dots = styled.div`
  @keyframes dot-keyframes {
    0% {
      opacity: 0.2;
    }

    50% {
      opacity: 1;
    }

    100% {
      opacity: 0.2;
    }
  }

  animation: dot-keyframes 1.5s infinite ease-in-out;
  background-color: #fff;
  border-radius: 10px;
  display: inline-block;
  height: 6px;
  width: 6px;
  margin: 0 2px;

  &:nth-child(2) {
    animation-delay: 0.5s;
  }

  &:nth-child(3) {
    animation-delay: 1s;
  }
`;
const DotLoader = () => /*#__PURE__*/React.createElement(DotsContainer, null, /*#__PURE__*/React.createElement(Dots, null), /*#__PURE__*/React.createElement(Dots, null), /*#__PURE__*/React.createElement(Dots, null));

const pulseBlack = keyframes`
	0% {
		box-shadow: 0 0 0 0 rgba(25, 25, 15, 1);
    transform: scale(1);
	}
	
	70% {
		box-shadow: 0 0 0 16px rgba(25, 25, 15, 0);
    transform: scale(1);
	}
	
	100% {
		box-shadow: 0 0 0 0 rgba(25, 25, 15, 0);
    transform: scale(1);
  }
`;

const Triangle = styled.div`
  width: 0;
  height: 0;
  border-top: 5px solid;
  border-color: var(--pin-border-color);
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
  position: relative;
  margin-left: auto;
  margin-right: auto;
`;
const InnerTriangle = styled.div`
  position: absolute;
  top: -6.3px;
  left: -5px;
  width: 0;
  height: 0;
  border-top: 5px solid;
  border-color: var(--pin-background-color);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
`;
const RootColors = styled(Box)`
  --dark-pin-background-color: ${getThemeProp('global.greyPalette.800')};
  --dark-pin-border-color: white;
  --dark-pin-text-color: white;

  --pin-background-color: var(--dark-pin-background-color);
  --pin-border-color: var(--dark-pin-border-color);
  --pin-text-color: var(--pin-border-color);

  --out-of-policy-color: ${getTokenValue(THEME_TOKENS.base.color.warningBackground)};

  ${_ref => {
  let {
    isPreferred
  } = _ref;
  return isPreferred && css`
      --pin-background-color: ${getTokenValue(THEME_TOKENS.base.color.successBackground)};
      --pin-border-color: ${getTokenValue(THEME_TOKENS.base.color.successText)};
      --out-of-policy-color: var(--pin-border-color);
    `;
}};

  ${_ref2 => {
  let {
    isHovered
  } = _ref2;
  return isHovered && css`
      --out-of-policy-color: ${getThemeProp('global.warning--color')};
    `;
}};
`;
const ContentFlex = styled(Flex)`
  border-radius: 12px;
  border: 1px solid;
  padding: 4px 8px 4px 6px;
  border-color: var(--pin-border-color);
  background-color: var(--pin-background-color);
  color: var(--pin-text-color);
  align-items: center;
  justify-content: center;
  line-height: 0.5;
  min-width: 68px;
  cursor: pointer;
`;
const CardComponentPosition = styled.div`
  position: absolute;
  z-index: 999;
  bottom: ${_ref3 => {
  let {
    position: {
      bottom
    }
  } = _ref3;
  return bottom;
}};
  left: ${_ref4 => {
  let {
    position: {
      left
    }
  } = _ref4;
  return left;
}};
  top: ${_ref5 => {
  let {
    position: {
      top
    }
  } = _ref5;
  return top;
}};
  right: ${_ref6 => {
  let {
    position: {
      right
    }
  } = _ref6;
  return right;
}};
  display: flex;
`;
const CardComponentMobileContainer = styled(RootColors)`
  position: relative;
  z-index: 1;
  filter: drop-shadow(0px 2px 2px #424242);
`;
const CardComponentMobilePosition = styled.div`
  position: absolute;
  z-index: 999;
  bottom: 100px;
  width: 330px;
  filter: drop-shadow(0px 2px 2px #424242);
  margin: 0 auto;
  left: 0;
  right: 0;
  display: flex;
`;
const ContentContainerHoveredStyles = css`
  z-index: 1; // allows the marker to pop forward if initially covered by another marker

  ${InnerTriangle} {
    border-top: 5px solid;
    border-color: white;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
  }
  ${Triangle} {
    border-top: 5px solid;
    border-color: ${getThemeProp('global.link--color')};
    border-right: 5px solid transparent;
    border-left: 5px solid transparent;
    filter: none;
  }
  ${ContentFlex} {
    border-color: ${getThemeProp('global.link--color')};
    background-color: white;
    color: ${getThemeProp('global.link--color')};
    filter: none;
  }
`;
const IconContainer = styled.span``;
const ContentContainer = styled(RootColors)`
  --offsetPinX: -50%;
  --offsetPinY: -100%;

  --scaleOnHover: 1.3;
  white-space: nowrap;
  position: relative;
  transition: transform 0.1s;
  transform: translateX(var(--offsetPinX)) translateY(var(--offsetPinY));
  filter: drop-shadow(0px 2px 2px #424242);

  ${_ref7 => {
  let {
    isHovered,
    isActive
  } = _ref7;
  return (isHovered || isActive) && ContentContainerHoveredStyles;
}};

  ${_ref8 => {
  let {
    isHovered
  } = _ref8;
  return isHovered && css`
      transform: scale(var(--scaleOnHover)) translateX(calc(var(--offsetPinX) / var(--scaleOnHover)))
        translateY(calc(var(--offsetPinY) / var(--scaleOnHover) - 3px));
    `;
}};

  &:hover {
    ${ContentContainerHoveredStyles};

    ${ContentFlex} {
      background-color: ${getThemeProp('global.link--color')};
      border-color: white;
      color: white;
    }
    ${Triangle} {
      visibility: hidden;
    }
    ${IconContainer} {
      display: none;
    }

    .priceText {
      span {
        display: none;
      }
      &::before {
        content: 'Select';
      }
    }
  }

  ${_ref9 => {
  let {
    isHovered
  } = _ref9;
  return isHovered && css`
      ${ContentFlex} {
        animation: ${pulseBlack} 1.8s infinite;
      }
    `;
}};
`;
const WarningIconWrapper = styled.div`
  font-size: 13px;
  color: var(--out-of-policy-color);
`;
const defaultPosition = {
  bottom: '28px',
  left: '-101.8px',
  top: null,
  right: null
};

const Price = _ref => {
  let {
    isLoadingMarker,
    price,
    type,
    isSoldOut
  } = _ref;
  let priceContent;
  if (isSoldOut) {
    priceContent = /*#__PURE__*/React.createElement("span", null, "Sold Out");
  } else if (isLoadingMarker) {
    priceContent = /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(DotLoader, null));
  } else {
    priceContent = /*#__PURE__*/React.createElement("span", null, `$${formatUSCurrencyNum(price)}`);
  }
  return /*#__PURE__*/React.createElement(ContentFlex, null, isLoadingMarker ? null : type === 'outPolicy' && /*#__PURE__*/React.createElement(IconContainer, null, /*#__PURE__*/React.createElement(WarningIconWrapper, null, /*#__PURE__*/React.createElement(IconWarningTriangle, null))), /*#__PURE__*/React.createElement(Text, {
    className: "priceText",
    weight: "600",
    size: "14px",
    ml: SPACE.size2
  }, priceContent));
};
Price.propTypes = {
  price: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['inPolicy', 'outPolicy', 'saved']),
  isLoadingMarker: PropTypes.bool,
  isSoldOut: PropTypes.bool
};
const CustomHotelMarker = _ref2 => {
  let {
    type,
    price,
    lat,
    lng,
    clusterer,
    showMarkerList,
    CardComponent,
    CardComponentMobile,
    locationData,
    isHovered = false,
    isActive = false,
    onClickMarker,
    isLoadingMarker,
    clickedPropId,
    propId,
    onMobileMarkerClick,
    setClickedPropIdFalse,
    showRefundableRates,
    isSoldOut
  } = _ref2;
  const [isShown, setIsShown] = useState(!clusterer);
  const [showCard, setShowCard] = useState(false);
  const markerRef = useRef();
  const markerElRef = useRef();
  const isClusterHovered = useRef(false);
  const mapContainerRect = useRef(null);
  const [position, setPosition] = useState(defaultPosition);
  const {
    isPreferred
  } = locationData;
  useEffect(() => {
    if (!markerRef.current) return;
    if (clusterer) {
      const cluster = clusterer.getClusters();
      for (let i = 0; i < cluster.length; i++) {
        if (cluster[i].markers.includes(markerRef.current)) {
          const icon = cluster[i].clusterIcon;
          const {
            div
          } = icon;
          if (div !== null && div !== void 0 && div.classList && isHovered) {
            div.classList.add('hovered');
            isClusterHovered.current = true;
          } else if (div !== null && div !== void 0 && div.classList && !isHovered && isClusterHovered.current) {
            div.classList.remove('hovered');
            isClusterHovered.current = false;
          }
          break;
        }
      }
    }
  }, [isHovered]);
  useEffect(() => {
    if (clusterer) {
      const cluster = clusterer.getClusters();
      for (let i = 0; i < cluster.length; i++) {
        if (cluster[i].markers.includes(markerRef.current)) {
          const icon = cluster[i].clusterIcon;
          const {
            div
          } = icon;
          if (div !== null && div !== void 0 && div.classList && isPreferred && !(div !== null && div !== void 0 && div.classList.contains('isPreferred'))) {
            div === null || div === void 0 || div.classList.add('isPreferred');
          }
          break;
        }
      }
    }
  });
  useEffect(() => {
    mapContainerRect.current = document.getElementById('map-container').getBoundingClientRect();
  }, []);
  const onLoad = marker => {
    markerRef.current = marker;
  };
  const showMarker = () => setIsShown(true);
  const hideMarker = () => setIsShown(false);
  const showCardTrue = () => {
    if (!showCard) setShowCard(true);
  };
  const showCardFalse = () => {
    if (showCard) setShowCard(false);
  };
  const onHover = () => {
    const markerElRefRect = markerElRef.current.getBoundingClientRect();
    const mapContainerRectCur = mapContainerRect.current;
    const cloneDefaultPosition = {
      ...defaultPosition
    };

    // Top bounds
    if (markerElRefRect.y - mapContainerRectCur.y <= 300) {
      cloneDefaultPosition.bottom = null;
      cloneDefaultPosition.top = '25px';
    }
    // Left bounds
    if (markerElRefRect.x - mapContainerRectCur.x <= 200) {
      cloneDefaultPosition.left = '0px';
    }
    // Right bounds
    if (mapContainerRectCur.x + mapContainerRectCur.width - markerElRefRect.x <= 200) {
      cloneDefaultPosition.left = null;
      cloneDefaultPosition.right = '0px';
    }
    setPosition(cloneDefaultPosition);
    showCardTrue();
  };
  useEffect(() => {
    if (!showMarkerList) return;
    for (let i = 0; i < showMarkerList.length; i++) {
      if (lat === showMarkerList[i].lat && lng === showMarkerList[i].lng) {
        showMarker();
        return;
      }
    }
    if (isShown) {
      hideMarker();
    }
  }, [showMarkerList]);
  const onClickHandler = isLoadingMarker ? () => {} : onClickMarker;
  const onMobileClickHandler = isLoadingMarker ? () => {} : onMobileMarkerClick;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.FineInput, null, /*#__PURE__*/React.createElement(Marker, {
    visible: false,
    position: {
      lat,
      lng
    },
    clusterer: clusterer,
    onLoad: onLoad
  }, isShown && /*#__PURE__*/React.createElement(OverlayView, {
    position: {
      lat,
      lng
    },
    mapPaneName: OverlayView.OVERLAY_MOUSE_TARGET
  }, /*#__PURE__*/React.createElement(ContentContainer, {
    width: "fit-content",
    isHovered: isHovered,
    isActive: isActive,
    isPreferred: isPreferred && !isLoadingMarker,
    onMouseEnter: onHover,
    onMouseLeave: showCardFalse,
    onClick: onClickHandler,
    ref: markerElRef
  }, CardComponent && showCard && /*#__PURE__*/React.createElement(CardComponentPosition, {
    position: position
  }, /*#__PURE__*/React.createElement(CardComponent, _extends({
    showRefundableRates: showRefundableRates
  }, locationData))), /*#__PURE__*/React.createElement(Price, {
    isLoadingMarker: isLoadingMarker || price === 0,
    type: type,
    price: price,
    isSoldOut: isSoldOut
  }), /*#__PURE__*/React.createElement(Triangle, {
    type: type
  }, /*#__PURE__*/React.createElement(InnerTriangle, {
    type: type
  })))))), /*#__PURE__*/React.createElement(MatchMedia.CoarseInput, null, /*#__PURE__*/React.createElement(Marker, {
    visible: false,
    position: {
      lat,
      lng
    },
    clusterer: clusterer,
    onLoad: onLoad
  }, isShown && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(OverlayView, {
    position: {
      lat,
      lng
    },
    mapPaneName: OverlayView.OVERLAY_MOUSE_TARGET
  }, /*#__PURE__*/React.createElement(CardComponentMobileContainer, {
    isPreferred: isPreferred && !isLoadingMarker,
    width: "fit-content",
    onClick: onMobileClickHandler
  }, /*#__PURE__*/React.createElement(Price, {
    isLoadingMarker: isLoadingMarker || price === 0,
    type: type,
    price: price,
    isSoldOut: isSoldOut
  }), /*#__PURE__*/React.createElement(Triangle, {
    type: type
  }, /*#__PURE__*/React.createElement(InnerTriangle, {
    type: type
  })))), propId === clickedPropId ? /*#__PURE__*/React.createElement(CardComponentMobilePosition, null, /*#__PURE__*/React.createElement(CardComponentMobile, _extends({
    showRefundableRates: showRefundableRates
  }, locationData, {
    onClick: onClickHandler,
    onClose: setClickedPropIdFalse
  }))) : null))));
};
CustomHotelMarker.propTypes = {
  price: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['inPolicy', 'outPolicy', 'saved']),
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  showMarkerList: PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  })),
  // eslint-disable-next-line react/forbid-prop-types
  clusterer: PropTypes.object,
  CardComponent: PropTypes.elementType,
  CardComponentMobile: PropTypes.elementType,
  locationData: PropTypes.shape({
    isPreferred: PropTypes.bool
  }),
  isHovered: PropTypes.bool,
  isActive: PropTypes.bool,
  isLoadingMarker: PropTypes.bool,
  onClickMarker: PropTypes.func,
  clickedPropId: PropTypes.string,
  propId: PropTypes.string,
  onMobileMarkerClick: PropTypes.func,
  setClickedPropIdFalse: PropTypes.func,
  showRefundableRates: PropTypes.bool,
  isSoldOut: PropTypes.bool
};

// eslint-disable-next-line react/prop-types
const MarkerClustererWithClassname$1 = _ref => {
  let {
    className,
    children,
    ...props
  } = _ref;
  return /*#__PURE__*/React.createElement(MarkerClusterer, _extends({
    clusterClass: className
  }, props), children);
};
const StyledClusterComponent$1 = styled(MarkerClustererWithClassname$1)`
  --dark-cluster-background-color: ${getThemeProp('global.greyPalette.800')};
  --dark-cluster-outline-color: white;
  --dark-cluster-text-color: white;

  --cluster-background-color: var(--dark-cluster-background-color);
  --cluster-outline-color: var(--dark-cluster-outline-color);
  --cluster-text-color: var(--cluster-outline-color);

  background-color: var(--cluster-background-color);
  outline: 1px solid var(--cluster-outline-color);
  border-radius: 50%;
  color: var(--cluster-text-color);
  transition: transform 0.1s;

  /* 
  Google Maps generates StyledClusterComponents in the DOM, which may be empty. 
  We hide the empty ones to avoid any artifacts from the styles. */
  :empty {
    display: none;
  }

  &:hover,
  &.hovered {
    --cluster-background-color: white;
    --cluster-outline-color: ${getThemeProp('global.link--color')};
    z-index: 1;
    transform: scale(1.3);
    box-shadow: 0px 2px 2px ${getThemeProp('global.greyPalette.800')};
  }

  &.hovered {
    animation: ${css`
      ${pulseBlack} 1.8s infinite;
    `};
  }
  &.isPreferred {
    --cluster-background-color: ${getTokenValue(THEME_TOKENS.base.color.successBackground)};
    --cluster-outline-color: ${getTokenValue(THEME_TOKENS.base.color.successBorder)};
  }
`;
const options$1 = {
  averageCenter: true,
  styles: [{
    height: 30,
    width: 30,
    url: 'https://cdn.amtrav.com/_.gif',
    textColor: 'inherit',
    textSize: 16
  }, {
    height: 40,
    width: 40,
    url: 'https://cdn.amtrav.com/_.gif',
    textColor: 'inherit',
    textSize: 16
  }, {
    height: 50,
    width: 50,
    url: 'https://cdn.amtrav.com/_.gif',
    textColor: 'inherit',
    textSize: 18
  }]
};
const CustomHotelMarkerClusterer = _ref2 => {
  let {
    hotels,
    minimumClusterSize = 5,
    CardComponent,
    CardComponentMobile,
    onClickMarker,
    isLoadingMarkers,
    activeHotelProperty,
    hoveredItem = {
      propId: null,
      lat: null,
      lng: null
    },
    showRefundableRates
  } = _ref2;
  const [showMarker, setShowMarker] = useState([]);
  const [clickedPropId, setClickedPropId] = useState(null);

  // debounce id tracker
  const timeoutId = useRef();
  const onClusteringEnd = _ref3 => {
    let {
      clusters
    } = _ref3;
    const currentId = setTimeout(() => {
      if (currentId === timeoutId.current) {
        const newShowMarker = [];
        clusters.forEach(_ref4 => {
          let {
            markers
          } = _ref4;
          if ((markers === null || markers === void 0 ? void 0 : markers.length) < minimumClusterSize) {
            markers.forEach(val => {
              newShowMarker.push({
                lat: val.position.lat(),
                lng: val.position.lng()
              });
            });
          }
        });
        setShowMarker(newShowMarker);
      }
    }, 100);
    timeoutId.current = currentId;
  };
  const onClickMarkerFunc = location => () => {
    onClickMarker(location);
  };
  const onMobileMarkerClick = location => () => {
    setClickedPropId(location.propId);
  };
  return /*#__PURE__*/React.createElement(StyledClusterComponent$1, {
    options: options$1,
    onClusteringEnd: onClusteringEnd,
    minimumClusterSize: minimumClusterSize,
    averageCenter: true
  }, clusterer => {
    return hotels.map(location => {
      const {
        inPolicy
      } = location;
      let type;
      if (inPolicy) {
        type = 'inPolicy';
      } else {
        type = 'outPolicy';
      }
      const {
        hasRefundableRate,
        averageNightlyRate,
        averageNightlyRateRefundable
      } = location;
      const hotelRate = showRefundableRates && hasRefundableRate ? averageNightlyRateRefundable : averageNightlyRate;
      return /*#__PURE__*/React.createElement(CustomHotelMarker, {
        price: +hotelRate.toFixed(0),
        showRefundableRates: showRefundableRates,
        lat: location.latitude,
        lng: location.longitude,
        clusterer: clusterer,
        showMarkerList: showMarker,
        locationData: location,
        CardComponent: CardComponent,
        CardComponentMobile: CardComponentMobile,
        key: location.propId,
        isLoadingMarker: isLoadingMarkers,
        isHovered: location.propId === hoveredItem.propId && !isLoadingMarkers,
        isActive: activeHotelProperty === location.propId,
        onClickMarker: onClickMarkerFunc(location),
        onMobileMarkerClick: onMobileMarkerClick(location),
        type: type,
        propId: location.propId,
        clickedPropId: clickedPropId,
        setClickedPropIdFalse: () => setClickedPropId(null),
        isSoldOut: location.soldOut
      });
    });
  });
};
CustomHotelMarkerClusterer.propTypes = {
  hotels: PropTypes.arrayOf(PropTypes.shape({})),
  minimumClusterSize: PropTypes.number,
  CardComponent: PropTypes.elementType,
  CardComponentMobile: PropTypes.elementType,
  onClickMarker: PropTypes.func,
  isLoadingMarkers: PropTypes.bool,
  hoveredItem: PropTypes.shape({
    propId: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  activeHotelProperty: PropTypes.string,
  showRefundableRates: PropTypes.bool
};

const CenterPointPopup = styled.div`
  position: absolute;
  top: -70px;
  left: 50%;
  transform: translate(-50%, 0);
  background: #424242;
  font-size: 16px;
  padding: 4px;
  filter: drop-shadow(0px 2px 2px #424242);
  font-weight: bold;
  color: #fff;
  border-radius: 4px;
  width: max-content;
`;
const LocationSearchMarker = _ref => {
  let {
    lat,
    lng,
    text,
    setShowTextTrue,
    showText
  } = _ref;
  return /*#__PURE__*/React.createElement(Marker, {
    position: {
      lat,
      lng
    },
    onClick: setShowTextTrue,
    zIndex: -99
  }, showText && /*#__PURE__*/React.createElement(OverlayView, {
    position: {
      lat,
      lng
    },
    mapPaneName: OverlayView.OVERLAY_LAYER
  }, showText && /*#__PURE__*/React.createElement(CenterPointPopup, null, text)));
};
LocationSearchMarker.propTypes = {
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  text: PropTypes.string,
  showText: PropTypes.bool.isRequired,
  setShowTextTrue: PropTypes.func.isRequired
};

const CustomCarMarker = _ref => {
  let {
    type,
    price,
    lat,
    lng,
    clusterer,
    showMarkerList,
    CardComponent,
    cardData,
    isHovered = false,
    isActive = false,
    onClickMarker,
    isLoadingMarker,
    isPreferred
  } = _ref;
  const [isShown, setIsShown] = useState(!clusterer);
  const [showCard, setShowCard] = useState(false);
  const markerRef = useRef();
  const markerElRef = useRef();
  const isClusterHovered = useRef(false);
  const mapContainerRect = useRef(null);
  const [position, setPosition] = useState(defaultPosition);
  useEffect(() => {
    if (!markerRef.current) return;
    if (clusterer) {
      const clusters = clusterer.getClusters();
      clusters.forEach(cluster => {
        if (cluster.markers.includes(markerRef.current)) {
          const icon = cluster.clusterIcon;
          const {
            div
          } = icon;
          if (div !== null && div !== void 0 && div.classList && isHovered) {
            div.classList.add('hovered');
            isClusterHovered.current = true;
          } else if (div !== null && div !== void 0 && div.classList && !isHovered && isClusterHovered.current) {
            div.classList.remove('hovered');
            isClusterHovered.current = false;
          }
        }
      });
    }
  }, [isHovered]);
  useEffect(() => {
    if (clusterer) {
      const clusters = clusterer.getClusters();
      clusters.forEach(cluster => {
        if (cluster.markers.includes(markerRef.current)) {
          const icon = cluster.clusterIcon;
          const {
            div
          } = icon;
          if (div !== null && div !== void 0 && div.classList && isPreferred && !(div !== null && div !== void 0 && div.classList.contains('isPreferred'))) {
            div === null || div === void 0 || div.classList.add('isPreferred');
          }
        }
      });
    }
  });
  useEffect(() => {
    mapContainerRect.current = document.getElementById('map-container').getBoundingClientRect();
  }, []);
  const onLoad = marker => {
    markerRef.current = marker;
  };
  const showMarker = () => setIsShown(true);
  const hideMarker = () => setIsShown(false);
  const showCardTrue = () => {
    if (!showCard) setShowCard(true);
  };
  const showCardFalse = () => {
    if (showCard) setShowCard(false);
  };
  const onHover = () => {
    const markerElRefRect = markerElRef.current.getBoundingClientRect();
    const mapContainerRectCur = mapContainerRect.current;
    const cloneDefaultPosition = {
      ...defaultPosition
    };

    // Top bounds
    if (markerElRefRect.y - mapContainerRectCur.y <= 300) {
      cloneDefaultPosition.bottom = null;
      cloneDefaultPosition.top = '25px';
    }
    // Left bounds
    if (markerElRefRect.x - mapContainerRectCur.x <= 200) {
      cloneDefaultPosition.left = '0px';
    }
    // Right bounds
    if (mapContainerRectCur.x + mapContainerRectCur.width - markerElRefRect.x <= 200) {
      cloneDefaultPosition.left = null;
      cloneDefaultPosition.right = '0px';
    }
    setPosition(cloneDefaultPosition);
    showCardTrue();
  };
  useEffect(() => {
    if (!showMarkerList) return;
    for (let i = 0; i < showMarkerList.length; i++) {
      if (lat === showMarkerList[i].lat && lng === showMarkerList[i].lng) {
        showMarker();
        return;
      }
    }
    if (isShown) {
      hideMarker();
    }
  }, [showMarkerList]);
  const generateIcon = () => {
    if (type === 'outPolicy') {
      return /*#__PURE__*/React.createElement(WarningIconWrapper, null, /*#__PURE__*/React.createElement(IconWarningTriangle, null));
    }
    return null;
  };
  const onClickHandler = isLoadingMarker ? () => {} : onClickMarker;
  return /*#__PURE__*/React.createElement(Marker, {
    visible: false,
    position: {
      lat,
      lng
    },
    clusterer: clusterer,
    onLoad: onLoad
  }, isShown && /*#__PURE__*/React.createElement(OverlayView, {
    position: {
      lat,
      lng
    },
    mapPaneName: OverlayView.OVERLAY_MOUSE_TARGET
  }, /*#__PURE__*/React.createElement(ContentContainer, {
    isPreferred: isPreferred && !isLoadingMarker,
    width: "fit-content",
    isHovered: isHovered,
    isActive: isActive,
    onMouseEnter: onHover,
    onMouseLeave: showCardFalse,
    onClick: onClickHandler,
    ref: markerElRef
  }, CardComponent && showCard && /*#__PURE__*/React.createElement(CardComponentPosition, {
    position: position
  }, /*#__PURE__*/React.createElement(CardComponent, {
    locationData: cardData
  })), /*#__PURE__*/React.createElement(ContentFlex, null, isLoadingMarker ? null : /*#__PURE__*/React.createElement(IconContainer, null, generateIcon()), /*#__PURE__*/React.createElement(Text, {
    className: "priceText",
    weight: "600",
    size: "14px",
    ml: SPACE.size2
  }, isLoadingMarker ? /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(DotLoader, null)) : /*#__PURE__*/React.createElement("span", null, "$", formatUSCurrencyNum(price)))), /*#__PURE__*/React.createElement(Triangle, null, /*#__PURE__*/React.createElement(InnerTriangle, null)))));
};
CustomCarMarker.propTypes = {
  price: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['inPolicy', 'outPolicy', 'saved']),
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  showMarkerList: PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  })),
  // eslint-disable-next-line react/forbid-prop-types
  clusterer: PropTypes.object,
  CardComponent: PropTypes.elementType,
  // CardComponentMobile: PropTypes.elementType,
  cardData: PropTypes.shape({}),
  zIndexControl: PropTypes.shape({
    val: PropTypes.number,
    increment: PropTypes.func
  }),
  isHovered: PropTypes.bool,
  isActive: PropTypes.bool,
  isLoadingMarker: PropTypes.bool,
  onClickMarker: PropTypes.func,
  isPreferred: PropTypes.bool
};

// eslint-disable-next-line react/prop-types
const MarkerClustererWithClassname = _ref => {
  let {
    className,
    children,
    ...props
  } = _ref;
  return /*#__PURE__*/React.createElement(MarkerClusterer, _extends({
    clusterClass: className
  }, props), children);
};
const StyledClusterComponent = styled(MarkerClustererWithClassname)`
  --dark-cluster-background-color: ${getThemeProp('global.greyPalette.800')};
  --dark-cluster-outline-color: white;
  --dark-cluster-text-color: white;

  --cluster-background-color: var(--dark-cluster-background-color);
  --cluster-outline-color: var(--dark-cluster-outline-color);
  --cluster-text-color: var(--cluster-outline-color);

  background-color: var(--cluster-background-color);
  outline: 1px solid var(--cluster-outline-color);
  border-radius: 50%;
  color: var(--cluster-text-color);
  transition: transform 0.1s;

  /* 
  Google Maps generates StyledClusterComponents in the DOM, which may be empty. 
  We hide the empty ones to avoid any artifacts from the styles. */
  :empty {
    display: none;
  }

  &:hover,
  &.hovered {
    --cluster-background-color: white;
    --cluster-outline-color: ${getThemeProp('global.link--color')};
    z-index: 1;
    transform: scale(1.3);
    box-shadow: 0px 2px 2px ${getThemeProp('global.greyPalette.800')};
  }

  &.hovered {
    animation: ${css`
      ${pulseBlack} 1.8s infinite;
    `};
  }
  &.isPreferred {
    --cluster-background-color: ${getTokenValue(THEME_TOKENS.base.color.successBackground)};
    --cluster-outline-color: ${getTokenValue(THEME_TOKENS.base.color.successBorder)};
  }
`;
const options = {
  averageCenter: true,
  styles: [{
    height: 30,
    width: 30,
    url: 'https://cdn.amtrav.com/_.gif',
    textColor: 'inherit',
    textSize: 16
  }, {
    height: 40,
    width: 40,
    url: 'https://cdn.amtrav.com/_.gif',
    textColor: 'inherit',
    textSize: 16
  }, {
    height: 50,
    width: 50,
    url: 'https://cdn.amtrav.com/_.gif',
    textColor: 'inherit',
    textSize: 18
  }]
};
const isInPolicyAndLowestPrice = location => {
  let inPolicy = false;
  let lowestPriceFound = 9999;
  location.cars.forEach(car => {
    var _car$policyViolations;
    lowestPriceFound = Math.min(lowestPriceFound, +car.averageDailyRate);
    if (!((_car$policyViolations = car.policyViolations) !== null && _car$policyViolations !== void 0 && _car$policyViolations.length) && !inPolicy) {
      inPolicy = true;
    }
  });
  return [inPolicy, lowestPriceFound];
};
const CustomCarMarkerClusterer = _ref2 => {
  let {
    cars,
    minimumClusterSize = 5,
    CardComponent,
    onClickMarker = () => {},
    hoveredItem = {
      lat: null,
      lng: null,
      dropoffLat: null,
      dropoffLng: null
    }
  } = _ref2;
  const [showMarker, setShowMarker] = useState([]);
  const groupedLocations = useMemo(() => {
    const groupedCarsByLatLon = []; // arrayOf groupByLatLonFields.
    const groupByLatLon = {};
    cars.forEach(location => {
      const [isInPolicy, lowestPriceFound] = isInPolicyAndLowestPrice(location);
      if (groupByLatLon[`${location.lat},${location.lon}`]) {
        const key = groupByLatLon[`${location.lat},${location.lon}`];
        key.vendorCodes.push(location.vendorCode);
        key.vendorLocIds.push(location.vendorLocId);
        key.isInPolicy = key.isInPolicy || isInPolicy;
        key.minPrice = Math.min(key.minPrice, lowestPriceFound);
        key.address = key.address || location.address;
        key.carsAvailable += location.cars.length;
        key.isPreferred = key.isPreferred || location.isPreferred;
      } else {
        groupByLatLon[`${location.lat},${location.lon}`] = {
          lat: location.lat,
          lon: location.lon,
          vendorCodes: [location.vendorCode],
          isInPolicy,
          minPrice: lowestPriceFound,
          address: location.address,
          carsAvailable: location.cars.length,
          distance: location.distance,
          vendorLocIds: [location.vendorLocId],
          isPreferred: location.isPreferred
        };
      }
      if (groupByLatLon[`${location.dropoffLat},${location.dropoffLon}`]) {
        const key = groupByLatLon[`${location.dropoffLat},${location.dropoffLon}`];
        if (!key.vendorCodes.includes(location.vendorCode)) {
          key.vendorCodes.push(location.vendorCode);
        }
        key.vendorLocIds.push(location.vendorLocId);
        key.isInPolicy = key.isInPolicy || isInPolicy;
        key.minPrice = Math.min(key.minPrice, lowestPriceFound);
        key.address = key.address || location.address;
        key.carsAvailable += location.cars.length;
        key.isPreferred = key.isPreferred || location.isPreferred;
      } else {
        groupByLatLon[`${location.dropoffLat},${location.dropoffLon}`] = {
          lat: location.dropoffLat,
          lon: location.dropoffLon,
          vendorCodes: [location.vendorCode],
          isInPolicy,
          minPrice: lowestPriceFound,
          indexes: [location.index],
          address: location.dropoffAddress,
          carsAvailable: location.cars.length,
          distance: null,
          vendorLocIds: [location.vendorLocId],
          isPreferred: location.isPreferred
        };
      }
    });
    Object.keys(groupByLatLon).forEach(key => {
      groupedCarsByLatLon.push(groupByLatLon[key]);
    });
    return groupedCarsByLatLon;
  }, [cars]);
  const onClusteringEnd = _ref3 => {
    let {
      clusters
    } = _ref3;
    const newShowMarker = [];
    clusters.forEach(_ref4 => {
      let {
        markers
      } = _ref4;
      if ((markers === null || markers === void 0 ? void 0 : markers.length) < minimumClusterSize) {
        markers.forEach(val => {
          newShowMarker.push({
            lat: val.position.lat(),
            lng: val.position.lng()
          });
        });
      }
    });
    setShowMarker(newShowMarker);
  };
  const onClickMarkerFunc = location => () => {
    onClickMarker(location);
  };
  if (!cars.length) return null;
  return /*#__PURE__*/React.createElement(StyledClusterComponent, {
    options: options,
    onClusteringEnd: onClusteringEnd,
    minimumClusterSize: minimumClusterSize,
    averageCenter: true
  }, clusterer => {
    return groupedLocations.map(location => {
      if (!location.lat && !location.lon) return null;
      return /*#__PURE__*/React.createElement(CustomCarMarker, {
        key: `${location.lat} + ${location.lon}`,
        price: Math.ceil(location.minPrice),
        lat: location.lat,
        lng: location.lon,
        clusterer: clusterer,
        showMarkerList: showMarker,
        type: location.isInPolicy ? 'inPolicy' : 'outPolicy',
        CardComponent: CardComponent,
        cardData: {
          ...location,
          startingPrice: location.minPrice
        },
        isHovered: location.lat === hoveredItem.lat && location.lon === hoveredItem.lng || location.lat === hoveredItem.dropoffLat && location.lon === hoveredItem.dropoffLng,
        onClickMarker: onClickMarkerFunc(location),
        isPreferred: location.isPreferred
      });
    });
  });
};
CustomCarMarkerClusterer.propTypes = {
  cars: PropTypes.arrayOf(PropTypes.shape({})),
  minimumClusterSize: PropTypes.number,
  CardComponent: PropTypes.elementType,
  onClickMarker: PropTypes.func,
  hoveredItem: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    dropoffLat: PropTypes.number,
    dropoffLng: PropTypes.number
  })
};

export { CustomCarMarkerClusterer, CustomHotelMarker, CustomHotelMarkerClusterer, GoogleMaps as GoogleMapLoader, LocationSearchMarker };
//# sourceMappingURL=index.esm.js.map
